import { useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSafeTwilioConversations } from '../../contexts/twilioConversationContext.shared';
import { FeatureFlag, useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { getUnreadNotificationCount } from '../../services/notificationService';
import { clearAppBadgeCount, setAppBadgeCount } from '../../services/appBadgeService';
import { computeAppBadgeCount } from '../../utils/appBadgeCount';
import { useNativeLifecycle } from '../useNativeLifecycle';

const UNREAD_EVENT = 'rintintin:notif-unread';
const PUSH_RECEIVED = 'native-push-received';
const POLL_MS = 30_000;

/**
 * Synchronise la pastille d'icône OS avec notifs + unread Conversations.
 */
export function useSyncAppIconBadge(): void {
  const { isAuthenticated } = useAuth();
  const { conversations } = useSafeTwilioConversations();
  const { isFeatureEnabled } = useFeatureFlags();
  const chatOn = isFeatureEnabled(FeatureFlag.CHAT_FEATURE);
  const notifOverrideRef = useRef<number | null>(null);

  const sync = useCallback(async () => {
    if (!isAuthenticated) {
      notifOverrideRef.current = null;
      await clearAppBadgeCount();
      return;
    }

    let notif = notifOverrideRef.current;
    if (notif == null) {
      notif = await getUnreadNotificationCount();
    }

    const total = computeAppBadgeCount(notif, conversations, {
      includeChat: chatOn,
    });
    await setAppBadgeCount(total);
  }, [chatOn, conversations, isAuthenticated]);

  useEffect(() => {
    void sync();
  }, [sync]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const id = window.setInterval(() => {
      notifOverrideRef.current = null;
      void sync();
    }, POLL_MS);

    const onUnread = (ev: Event) => {
      const detail = (ev as CustomEvent<number>).detail;
      if (typeof detail === 'number' && Number.isFinite(detail)) {
        notifOverrideRef.current = Math.max(0, detail);
        void sync();
      }
    };
    const onPush = () => {
      notifOverrideRef.current = null;
      void sync();
    };

    window.addEventListener(UNREAD_EVENT, onUnread);
    window.addEventListener(PUSH_RECEIVED, onPush);
    return () => {
      window.clearInterval(id);
      window.removeEventListener(UNREAD_EVENT, onUnread);
      window.removeEventListener(PUSH_RECEIVED, onPush);
    };
  }, [isAuthenticated, sync]);

  useNativeLifecycle({
    onResume: () => {
      notifOverrideRef.current = null;
      void sync();
    },
  });
}
